import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Plus, Tags, FolderOpen } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS } from "./types";

interface BulkMetadataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onApply: (updates: { folder?: string; tags?: string[]; mode: "replace" | "append" }) => void;
}

export default function BulkMetadataDialog({
  open,
  onOpenChange,
  selectedCount,
  onApply,
}: BulkMetadataDialogProps) {
  const { t } = useTranslation();
  const [category, setCategory] = useState<string>("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagMode, setTagMode] = useState<"replace" | "append">("append");

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
  };

  const handleApply = () => {
    onApply({
      folder: category || undefined,
      tags: tags.length > 0 ? tags : undefined,
      mode: tagMode,
    });
    setCategory("");
    setTags([]);
    setTagInput("");
    onOpenChange(false);
  };

  const hasChanges = category || tags.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5" />
            Edit {selectedCount} file{selectedCount > 1 ? "s" : ""}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Category */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              <FolderOpen className="h-3.5 w-3.5" /> Category
            </Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 text-xs">
                <SelectValue placeholder="Don't change category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat} className="text-xs">
                    {CATEGORY_LABELS[cat] || cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              <Tags className="h-3.5 w-3.5" /> Tags
            </Label>
            <div className="flex gap-1.5">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Add a tag..."
                className="h-9 text-xs flex-1"
              />
              <IconButton variant="outline" className="h-9 w-9" onClick={addTag} type="button" label={t("actions.addTag")}>
                <Plus className="h-3.5 w-3.5" />
              </IconButton>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-[10px] gap-1 pr-1">
                    {tag}
                    <button onClick={() => setTags(tags.filter((t) => t !== tag))} className="hover:text-destructive">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {tags.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant={tagMode === "append" ? "default" : "outline"}
                  size="sm"
                  className="text-[10px] h-7"
                  onClick={() => setTagMode("append")}
                >
                   Append to existing
                </Button>
                <Button
                  variant={tagMode === "replace" ? "default" : "outline"}
                  size="sm"
                  className="text-[10px] h-7"
                  onClick={() => setTagMode("replace")}
                >
                  Replace
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleApply} disabled={!hasChanges}>
            Apply to {selectedCount} file{selectedCount > 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
